import { FormErrors } from 'app/shared/validators/generic-validators';
import { MdDialogRef } from '@angular/material';
import { MapsAPILoader } from '@agm/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Component, ElementRef, NgZone, OnInit, ViewChild, Input, HostListener } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';

import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { unsavedFormCheck } from 'app/shared/validators/generic-validators';

import { ApplicationTypeConstants, WarmUpService } from 'app/services/warmup/warmup.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';

import { Address } from 'app/shared/models/address.model';
import { AddressInformationService } from 'app/entities/details/addressinformation/address-information.service';

@Component({
    selector: 'add-address',
    templateUrl: './add-address.component.html',
    styleUrls: ['./add-address.component.css']
})

/**
 * Component for Add/Update/Delete Address information.
 */
export class AddAddressComponent implements OnInit {
    public entityId: number;
    public latitude: number;
    public longitude: number;
    private addressForm: FormGroup;
    selectedAddress: Address;
    action: string;
    actionType: string;
    public zoom: number;
    addressTypes: ApplicationValue[];
    addressId: number;

    @ViewChild('search')
    public searchElementRef: ElementRef;

    formErrors = {
        'searchLocation': '',
        'houseNumber': '',
        'address': '',
        'zipcode': '',
        'addressType': '',
        'city': '',
        'country': ''
    };

    validationMessages = {
        'houseNumber': {
            'required': 'Required field value.'
        },
        'address': {
            'required': 'Required field value.'
        },
        'zipcode': {
            'required': 'Required field value.'
        },
        'addressType': {
            'required': 'Required field value.'
        },
        'city': {
            'required': 'Required field value.'
        },
        'country': {
            'required': 'Required field value.'
        }
    };

    constructor(
        private mapsAPILoader: MapsAPILoader,
        private ngZone: NgZone,
        public dialogRef: MdDialogRef<any>,
        private formBuilder: FormBuilder,
        private addressInformationService: AddressInformationService,
        private trackingService: TrackingService,
        private snackBarService: SnackBarService,
        private warmUpService: WarmUpService,
    ) { }

    ngOnInit() {
        this.buildForm();
        // set google maps defaults
        this.zoom = 4;
        this.latitude = 39.8282;
        this.longitude = -98.5795;

        // set current position
        this.setCurrentPosition();
        // Get Address Types
        this.getAddressTypes();
        this.updateAdddressForm(true);
        this.actionType = this.action === 'Add' && 'New' || 'Edit';

        // load Places Autocomplete
        this.mapsAPILoader.load().then(() => {
            const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
                types: ['address']
            });
            autocomplete.addListener('place_changed', () => {
                this.ngZone.run(() => {
                    // get the place result
                    const place: google.maps.places.PlaceResult = autocomplete.getPlace();
                    this.extractAddressInformation(place);
                    this.updateAdddressForm(false);

                    // verify result
                    if (place.geometry === undefined || place.geometry === null) {
                        return;
                    }

                    // set latitude, longitude and zoom
                    this.latitude = place.geometry.location.lat();
                    this.longitude = place.geometry.location.lng();
                    this.zoom = 12;
                });
            });
        });
    }

    buildForm() {
        this.addressForm = this.formBuilder.group({
            searchLocation: '',
            houseNumber: '',
            address: '',
            city: '',
            zipcode: '',
            addressType: ['', [Validators.required]],
            country: ['', [Validators.required]],
        });
        this.addressForm.valueChanges.subscribe(data => this.onValueChanged(data));
    }

    onValueChanged(data?: any) {
        if (!this.addressForm) {
            return;
        }
        FormErrors('addressForm', this);
    }

    private setCurrentPosition() {
        if ('geolocation' in navigator && this.action === 'Add') {
            navigator.geolocation.getCurrentPosition((position) => {
                this.zoom = 12;
            });
        }
    }

    getAddressTypes() {
        const addressTypes =
            this.warmUpService.getApplicationValue(ApplicationTypeConstants.ADDRESS_TYPE);
        if (addressTypes instanceof Observable) {
            addressTypes.subscribe(
                response => {
                    this.warmUpService.setApplicationTypeInLS(response);
                    this.addressTypes = response.values;
                }
            );
        } else {
            this.addressTypes = addressTypes;
        }
    }

    onSubmit({ value, valid }) {
        this.addressForm.disable();
        const addressModel = new Address();
        // Address Id only if action is Edit
        if (this.addressId) {
            addressModel.id = this.addressId;
        }
        addressModel.entityId = this.entityId;
        addressModel.houseNumber = value.houseNumber;
        addressModel.street = value.address;
        addressModel.zip = value.zipcode;
        addressModel.city = value.city;
        addressModel.country = value.country;
        addressModel.other = '';
        addressModel.addressType = value.addressType;
        const completeAddress = this.getCompleteAddress(addressModel);
        this.addressInformationService.getLatLngFromAddress(completeAddress)
            .subscribe(latLng => {
                addressModel.lat = latLng.lat();
                addressModel.lng = latLng.lng();
                this.addressInformationService.saveAddressInformation(addressModel, this.action)
                    .subscribe(response => {
                        if (response.flagResult) {
                            this.dialogRef.close(addressModel);
                        }
                        this.trackingService
                            .emitEvent(
                            TrackingConstants.ADDRESS_INFO_UPDATE,
                            TrackingConstants.PERFORM_ADDRESS_INFO_UPDATE,
                            TrackingConstants.SUCCESS, 1);
                        this.callSnackBarService(response, this.action);
                    }, err => {
                        this.trackingService
                            .emitEvent(
                            TrackingConstants.ADDRESS_INFO_UPDATE,
                            TrackingConstants.PERFORM_ADDRESS_INFO_UPDATE,
                            TrackingConstants.FAILURE, 0);
                        this.callSnackBarService(err, this.action);
                    });
            });
    }

    getCompleteAddress(addressModel: Address) {
        return addressModel.houseNumber + ' ' + addressModel.street + ' ' + addressModel.city
            + ' ' + addressModel.country + ' ' + addressModel.zip;
    }

    updateAdddressForm(updateLocation: boolean): void {
        if (this.selectedAddress) {
            this.addressId = this.selectedAddress.id;

            if ('geolocation' in navigator && updateLocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                    this.latitude = this.selectedAddress.lat;
                    this.longitude = this.selectedAddress.lng;
                    this.zoom = 12;
                });
            }

            this.addressForm.patchValue({
                houseNumber: this.selectedAddress.houseNumber,
                address: this.selectedAddress.street,
                zipcode: this.selectedAddress.zip,
                city: this.selectedAddress.city,
                country: this.selectedAddress.country,
                addressType: this.selectedAddress.addressType,
            });
        }
    }

    extractAddressInformation(place) {
        if (!this.selectedAddress) {
            this.selectedAddress = new Address();
        }

        this.selectedAddress.houseNumber = '';
        this.selectedAddress.street = '';
        this.selectedAddress.city = '';
        this.selectedAddress.country = '';
        this.selectedAddress.zip = '';

        for (const value of place.address_components) {
            if (['street_number', 'floor', 'sublocality_level_3'].indexOf(value.types[0]) >= 0) {
                this.addAddressValue('houseNumber', value.long_name);
            } else if (['route', 'sublocality'].indexOf(value.types[0]) >= 0) {
                this.addAddressValue('street', value.long_name);
            } else if (['locality'].indexOf(value.types[0]) >= 0) {
                this.addAddressValue('city', value.long_name);
            } else if (['country'].indexOf(value.types[0]) >= 0) {
                this.addAddressValue('country', value.long_name);
            } else if (['postal_code'].indexOf(value.types[0]) >= 0) {
                this.addAddressValue('zip', value.long_name);
            }
        }
    }

    addAddressValue(type: string, value: string) {
        this.selectedAddress[type] += (value + ' ');
    }

    callSnackBarService(response: any, operation: string) {
        this.snackBarService
            .showStatus(response,
            operation === 'Add'
            && MessageService.ADDRESS_INFO_ADD_SUCCESS || MessageService.ADDRESS_INFO_UPDATE_SUCCESS,
            MessageService.ADDRESS_INFO_FAILURE);
    }

    @HostListener('keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        unsavedFormCheck(event, this.dialogRef, this.addressForm);
    }
}



// WEBPACK FOOTER //
// ./src/app/entities/details/addressinformation/addaddress/add-address.component.ts